
import { Award, ArrowRight, Clock, Wrench, Droplet, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const testimonials = [
  {
    id: 1,
    name: "Kitchen Sink Customer",
    location: "Denver Metro, CO",
    rating: 5,
    text: "Nick from Colorado First Plumbing and Sewer did an amazing job on a stoppage in my kitchen sink! He was very professional and personable. He even gave me tips to keep the sink running smoothly. I mentioned an issue with a toilet running so he took a look and fixed in no time! I would HIGHLY recommend them for your plumbing needs!",
    source: "Angi",
    initial: "N",
    bgColor: "bg-gradient-to-r from-blue-800 to-blue-900"
  },
  {
    id: 2,
    name: "Emergency Sewer Customer",
    location: "Denver, CO", 
    rating: 5,
    text: "Codee came out on short notice when the City of Denver mandated we needed a sewer scope within 48 hours (!?). While he was out we had him estimate a few other issues that we're having, which he did. He explained everything really clearly and gave me some suggestions for saving money and timing some of the things that we should have done but aren't absolutely critical right now. He charged me exactly what the city was willing to reimburse, which was great. All in all a very nice guy.",
    source: "Angi",
    initial: "C",
    bgColor: "bg-gradient-to-r from-orange-500 to-red-600"
  },
  {
    id: 3,
    name: "Reece T.",
    location: "Littleton, CO",
    rating: 5,
    text: "I had two running toilets and a leaking faucet and Reese came out and fixed both with ease. He was very friendly and helpful. Also kept me in the loop the entire time. Thanks Reese!",
    source: "BirdEye",
    initial: "R", 
    bgColor: "bg-gradient-to-r from-green-600 to-green-500"
  },
  {
    id: 4,
    name: "",
    location: "Lakewood, CO",
    rating: 5,
    text: "Nick, one of the owners couldn't have been nicer, he determined quickly it was definitely my guest bath toilet that had been plunged by my dogsitter while I was away on vacation. We had some issues getting the toilet from Home Depot (it arrived cracked!) but they were terrific in communicating, finding it from another source, scheduling promptly and just all around really nice guys.",
    source: "Angi",
    initial: "H",
    bgColor: "bg-gradient-to-r from-blue-600 to-blue-800"
  },
  {
    id: 5, 
    name: "Codee Customer",
    location: "Centennial, CO",
    rating: 5,
    text: "I was impressed with Codee's knowledge and quick responses, plus he was a delight to work with. I would call him again in a heartbeat were I to need plumbing work in the future.",
    source: "HomeAdvisor",
    initial: "C",
    bgColor: "bg-gradient-to-r from-green-600 to-green-500"
  }
];

const Testimonials = () => {
  // Add structured data for SEO
  useEffect(() => {
    const reviewSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      'name': 'Colorado First Plumbing and Sewer LLC',
      'image': 'https://colorado-plumbing.com/colorado-plumbing-uploads/coloradofirst_logo.png',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Highlands Ranch',
        'addressRegion': 'CO',
        'addressCountry': 'US'
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '4.8',
        'reviewCount': '360',
        'bestRating': '5',
        'worstRating': '1'
      },
      'review': [
        {
          '@type': 'Review',
          'author': {
            '@type': 'Person',
            'name': 'Kitchen Sink Customer'
          },
          'reviewRating': {
            '@type': 'Rating',
            'ratingValue': '5',
            'bestRating': '5'
          },
          'reviewBody': 'Nick from Colorado First Plumbing and Sewer did an amazing job on a stoppage in my kitchen sink! He was very professional and personable. I would HIGHLY recommend them for your plumbing needs!',
          'publisher': {
            '@type': 'Organization',
            'name': 'Angi'
          }
        },
        {
          '@type': 'Review',
          'author': {
            '@type': 'Person',
            'name': 'Emergency Sewer Customer'
          },
          'reviewRating': {
            '@type': 'Rating',
            'ratingValue': '5',
            'bestRating': '5'
          },
          'reviewBody': 'Codee came out on short notice when the City of Denver mandated we needed a sewer scope within 48 hours. He explained everything really clearly and gave me some suggestions for saving money. All in all a very nice guy.',
          'publisher': {
            '@type': 'Organization',
            'name': 'Angi'
          }
        }
      ]
    };

    // Inject structured data into page head
    let schemaScript = document.querySelector('#testimonials-review-schema');
    if (schemaScript) {
      schemaScript.textContent = JSON.stringify(reviewSchema);
    } else {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('id', 'testimonials-review-schema');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.textContent = JSON.stringify(reviewSchema);
      document.head.appendChild(schemaScript);
    }

    // Load BirdEye widget script dynamically
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) {
        return;
      }

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget script loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget script');
        // Show fallback content if script fails
        const fallbackDiv = document.querySelector('#birdeye-fallback');
        if (fallbackDiv) {
          fallbackDiv.style.display = 'block';
        }
      };

      document.head.appendChild(script);
    };

    // Load the widget script
    loadBirdEyeWidget();

    return () => {
      const existingScript = document.querySelector('#testimonials-review-schema');
      if (existingScript) {
        existingScript.remove();
      }
      
      const birdeyeScript = document.querySelector('#birdeye-widget-script');
      if (birdeyeScript) {
        birdeyeScript.remove();
      }
    };
  }, []);

  return (
    <section id="testimonials" className="relative py-16 md:py-20 bg-gradient-to-br from-blue-800 to-blue-900">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-block px-4 py-2 mb-6 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm border border-blue-200">
            ⭐ Customer Reviews
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Colorado Customers Love Our Service
          </h2>
          <p className="text-lg text-blue-100">
            See why Colorado residents trust Colorado First Plumbing for their emergency and residential plumbing needs
          </p>
          {/* Review Platform Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {/* Google Reviews Card */}
            <a 
              href="https://g.co/kgs/e75VoQk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl cursor-pointer block"
              style={{ pointerEvents: 'auto' }}
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <span className="text-white font-bold text-lg">G</span>
                </div>
                <h3 className="font-bold text-white mb-2">Google Reviews</h3>
                <div className="flex justify-center space-x-1 text-yellow-400 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Award key={i} size={20} className="fill-current" />
                  ))}
                </div>
                <div className="text-2xl font-bold text-white mb-1">4.8</div>
                <div className="text-blue-200 text-sm">(360+ reviews)</div>
              </div>
            </a>

            {/* BirdEye Reviews Card */}
            <a 
              href="https://birdeye.com/colorado-first-plumbing-and-sewer-172384402926614" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl cursor-pointer block"
              style={{ pointerEvents: 'auto' }}
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <h3 className="font-bold text-white mb-2">BirdEye Reviews</h3>
                <div className="flex justify-center space-x-1 text-yellow-400 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Award key={i} size={20} className="fill-current" />
                  ))}
                </div>
                <div className="text-2xl font-bold text-white mb-1">5.0</div>
                <div className="text-blue-200 text-sm">(295+ reviews)</div>
              </div>
            </a>

            {/* HomeAdvisor Reviews Card */}
            <a 
              href="https://www.homeadvisor.com/rated.CFPPlumbingLLC.63409840.html#reviews" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl cursor-pointer block"
              style={{ pointerEvents: 'auto' }}
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <span className="text-white font-bold text-lg">H</span>
                </div>
                <h3 className="font-bold text-white mb-2">HomeAdvisor</h3>
                <div className="flex justify-center space-x-1 text-yellow-400 mb-2">
                  {[...Array(4)].map((_, i) => (
                    <Award key={i} size={20} className="fill-current" />
                  ))}
                  <div className="relative">
                    <Award size={20} className="text-gray-400" />
                    <div className="absolute top-0 left-0 overflow-hidden w-4/5">
                      <Award size={20} className="text-yellow-400 fill-current" />
                    </div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-white mb-1">4.8</div>
                <div className="text-blue-200 text-sm">(92 reviews)</div>
              </div>
            </a>
          </div>
        </div>
        
        {/* BirdEye Reviews Widget with SEO Enhancement */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-xl p-6 shadow-2xl border border-gray-200 max-w-4xl w-full">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Real Customer Reviews</h3>
              <p className="text-gray-600">See what Colorado customers say about our plumbing services</p>
              
              {/* SEO-friendly review summary */}
              <div className="mt-4 text-sm text-gray-600">
                <span className="font-semibold text-gray-800">4.8★ average rating</span> from 
                <span className="font-semibold text-gray-800"> 295+ verified customers</span> across 
                <span className="font-semibold text-gray-800"> Denver Metro</span> area
              </div>
            </div>
            
            {/* BirdEye Widget - Loaded Dynamically */}
            <div id="bf-revz-widget-987654321301115183" aria-label="Customer Reviews Widget for Colorado First Plumbing"></div>
            
            {/* Fallback Content - Shows if widget fails to load */}
            <div id="birdeye-fallback" style={{ display: 'none' }} className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-bold text-gray-800 mb-2">Customer Reviews Loading...</h4>
              <p className="text-gray-600 mb-4">
                If reviews don't appear, you can view all 295+ verified customer reviews directly on our BirdEye page.
              </p>
              <a 
                href="https://birdeye.com/colorado-first-plumbing-and-sewer-172384402926614" 
                className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                target="_blank" 
                rel="noopener noreferrer"
              >
                View All Reviews →
              </a>
            </div>
            
            {/* SEO Fallback Content for Search Engines */}
            <noscript>
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <h4 className="font-bold text-gray-800 mb-2">Colorado First Plumbing Customer Reviews</h4>
                <p className="text-gray-600 mb-4">
                  Colorado First Plumbing has earned a 4.8-star rating from 295+ verified customers across 
                  Highlands Ranch, Littleton, Denver, Lakewood, Arvada, Thornton, and surrounding areas.
                </p>
                <p className="text-gray-600">
                  Customers consistently praise our transparent pricing, emergency response times, 
                  professional sewer repair services, and master plumber expertise.
                </p>
                <a 
                  href="https://birdeye.com/colorado-first-plumbing-and-sewer-172384402926614" 
                  className="inline-block mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  View All Reviews →
                </a>
              </div>
            </noscript>
          </div>
        </div>
        
        {/* Recent Projects Showcase - AUTHORITY BUILDING */}
        <div className="mt-12 bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4 text-white">Recent Colorado Plumbing Projects</h3>
            <p className="text-blue-100">
              Professional plumbing and sewer projects completed across Colorado neighborhoods
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-800 to-blue-900 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Droplet size={32} className="text-white" />
              </div>
              <h4 className="font-bold mb-2 text-gray-800">Complete Sewer Replacement</h4>
              <p className="text-sm text-gray-600">Highlands Ranch • Trenchless method • 2-day completion</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Clock size={32} className="text-white" />
              </div>
              <h4 className="font-bold mb-2 text-gray-800">Emergency Water Line Repair</h4>
              <p className="text-sm text-gray-600">Littleton area • Sunday evening • Fixed in 3 hours</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Wrench size={32} className="text-white" />
              </div>
              <h4 className="font-bold mb-2 text-gray-800">Master Bathroom Remodel</h4>
              <p className="text-sm text-gray-600">Denver Metro • Complete plumbing upgrade • Luxury fixtures</p>
            </div>
          </div>
        </div>
        
        {/* Social Proof CTA */}
        <div className="mt-12 text-center">
          <p className="text-lg text-blue-100 mb-6">
            Join <span className="font-bold text-green-400">satisfied Colorado customers</span> who trust Colorado First Plumbing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="/pricing"
              className="relative bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-300 hover:via-orange-400 hover:to-red-400 text-white px-8 py-4 rounded-full text-xl font-black inline-flex items-center transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-[1.08] ring-4 ring-yellow-300/50 hover:ring-yellow-200/70 animate-gradient-shift"
              style={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                boxShadow: '0 0 30px rgba(251, 191, 36, 0.6), 0 0 60px rgba(251, 191, 36, 0.4), 0 20px 40px rgba(0,0,0,0.3)',
                backgroundSize: '300% 300%',
                animation: 'gradient-shift 3s ease-in-out infinite'
              }}
            >
              <span className="relative z-10 flex items-center">
                💰 See Pricing Now
                <ExternalLink size={20} className="ml-3" />
              </span>
            </a>
            <Link 
              to="/reviews"
              className="text-blue-200 hover:text-white font-semibold inline-flex items-center space-x-2 hover:underline transition-colors"
            >
              <span>⭐ View Our Online Reviews</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
